import { ParagraphNode, TextNode, LexicalEditor, LexicalNode, DOMExportOutput } from 'lexical';
import { QuoteNode, HeadingNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { TableNode, TableRowNode, TableCellNode } from '@lexical/table';
import { LinkNode } from '@lexical/link';

function stripEditorClasses(el: HTMLElement)
{
    const cls = el.getAttribute('class');
    if (!cls) { return; }
    const kept = cls
        .split(/\s+/)
        .filter((c) => c && !/^editor-/.test(c) && c !== 'ltr' && c !== 'rtl')
        .join(' ');
    if (kept) { el.setAttribute('class', kept); }
    else { el.removeAttribute('class'); }
}

function wrap(): (editor: LexicalEditor, node: LexicalNode) => DOMExportOutput
{
    return (editor, node) =>
    {
        const out = (node as any).exportDOM(editor) as DOMExportOutput;
        const el = out.element;
        if (el instanceof HTMLElement) { stripEditorClasses(el); }
        return out;
    };
}

export function constructExportMap(): Map<any, (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput>
{
    const exporter = wrap();
    const map = new Map<any, (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput>();
    map.set(ParagraphNode as any, exporter);
    map.set(HeadingNode as any, exporter);
    map.set(QuoteNode as any, exporter);
    map.set(ListNode as any, exporter);
    // Custom exporter for <li>: drop redundant value="1"
    map.set(ListItemNode as any, (editor, node) =>
    {
        const out = (node as any).exportDOM(editor) as DOMExportOutput;
        const el = out.element;
        if (el instanceof HTMLElement)
        {
            stripEditorClasses(el);
            if (el.tagName.toLowerCase() === 'li')
            {
                const val = el.getAttribute('value');
                if (val === '1') { el.removeAttribute('value'); }
            }
        }
        return out;
    });
    map.set(TableNode as any, exporter);
    map.set(TableRowNode as any, exporter);
    map.set(TableCellNode as any, exporter);
    map.set(LinkNode as any, exporter);
    // Avoid wrapping plain text in <span style="white-space: pre-wrap;"> when no formatting/styles
    map.set(TextNode as any, (editor, node) =>
    {
        const t = node as unknown as TextNode;
        const hasFormat = t.getFormat() !== 0;
        const hasStyle = (t.getStyle() || '').length > 0;
    const content = t.getTextContent();
    const needsPreWrap = /\n/.test(content) || /\s{2,}/.test(content) || /^\s/.test(content) || /\s$/.test(content);
    const isOnlyNbsp = /^[\u00A0]+$/.test(content);
    if (!hasFormat && !hasStyle && (!needsPreWrap || isOnlyNbsp))
        {
            const placeholder = document.createElement('span');
            return {
                element: placeholder,
                after: () => document.createTextNode(content),
            } as DOMExportOutput;
        }
        return exporter(editor, node);
    });
    return map;
}

export function cleanExportedHtml(html: string): string
{
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const walk = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
    const allowedStyle = new Set(['text-align','color','background-color','font-size','font-weight','font-style','text-decoration']);

    while (walk.nextNode())
    {
        const el = walk.currentNode as HTMLElement;

        // 1) usuń klasy „edytorowe”
        if (el.className)
        {
            const kept = el.className.split(/\s+/).filter(c => !c.startsWith('editor-') && !c.startsWith('lexical-'));
            if (kept.length) { el.className = kept.join(' '); } else { el.removeAttribute('class'); }
        }

        // 2) przytnij style do whitelisty (albo usuń całkiem, jeśli nie chcesz styli inline)
        if (el.hasAttribute('style'))
        {
            const kept: string[] = [];
            el.getAttribute('style')!.split(';').forEach(rule =>
            {
                const [prop,val] = rule.split(':').map(s => s?.trim());
                if (prop && val && allowedStyle.has(prop)) { kept.push(`${prop}: ${val}`); }
            });
            if (kept.length) { el.setAttribute('style', kept.join('; ')); } else { el.removeAttribute('style'); }
        }

        // 3) usuń data-* i inne techniczne
        [...el.attributes].forEach(a => {
                if (a.name.startsWith('data-') || a.name === 'dir') { el.removeAttribute(a.name); }
            });
    }
    return doc.body.innerHTML;
}